import * as React from "react";
import { cn } from "@/lib/utils";

export interface CounterProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
}

const Counter = React.forwardRef<HTMLDivElement, CounterProps>(
  ({ value, onChange, min = 0, max = 99, className }, ref) => {
    const handleDecrement = () => {
      if (value > min) {
        onChange(value - 1);
      }
    };

    const handleIncrement = () => {
      if (value < max) {
        onChange(value + 1);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(e.target.value, 10);
      if (!isNaN(newValue) && newValue >= min && newValue <= max) {
        onChange(newValue);
      }
    };

    return (
      <div ref={ref} className={cn("flex items-center gap-1", className)}>
        <button
          type="button"
          onClick={handleDecrement}
          disabled={value <= min}
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-md text-lg font-bold transition-all",
            "bg-bb-gray-mid border-2 border-bb-gray-mid text-bb-bone-white",
            "hover:border-bb-gold hover:bg-bb-gray-dark",
            "active:bg-bb-blood-red",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-bb-gray-mid disabled:hover:bg-bb-gray-mid"
          )}
          aria-label="Decrement"
        >
          −
        </button>
        <input
          type="number"
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          className={cn(
            "h-9 w-12 rounded-md text-center text-lg font-semibold",
            "bg-bb-gray-mid border-2 border-bb-gray-mid text-bb-bone-white",
            "focus:border-bb-gold focus:outline-none",
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          )}
          aria-label="Value"
        />
        <button
          type="button"
          onClick={handleIncrement}
          disabled={value >= max}
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-md text-lg font-bold transition-all",
            "bg-bb-gray-mid border-2 border-bb-gray-mid text-bb-bone-white",
            "hover:border-bb-gold hover:bg-bb-gray-dark",
            "active:bg-bb-blood-red",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-bb-gray-mid disabled:hover:bg-bb-gray-mid"
          )}
          aria-label="Increment"
        >
          +
        </button>
      </div>
    );
  }
);
Counter.displayName = "Counter";

export { Counter };
